import type { Partner } from "@expert/shared-types";

import { truncateAnalyticsText } from "@asurion-core/eventualize-react";
import { getFeature } from "@asurion-core/expert-workspace-feature-flagging";
import { useExpertNoteStore } from "@asurion-core/expert-workspace-notepad";
import { removePii } from "@asurion-core/expert-workspace-pii";
import { getAppConfigSingleton } from "@expert/app-config";
import { getLogger } from "@expert/logging";
import { trackFullStoryEvent } from "@expert/monitoring";
import {
    clearLanguageStoreStorage,
    EligibilityStatus,
    getMappedIntent,
    useCallDetailsStore,
    useDetectedCuesStore,
    useIntegratedFlowStore,
    useOfferChecklistStore,
    useProductOfferStore,
    useVerificationFlowStore,
    useVerifiedCustomerStore,
} from "@expert/sales-tools";
import { clearProbedSession, useAssistPanelStore, useDeviceInfoStore, useGuideStore } from "@expert/solve-tools";

import type { Session } from "@/sdk/sessions/types";

import { getRootDispatcher } from "@/analytics";
import {
    clearC360CustomerSession,
    endC360InteractionLine,
    linkC360CustomerAtCallStart,
    startC360Interaction,
} from "@/c360";
import { useCallCategoryStore } from "@/call-controls/hooks/callCategoryStore";
import { useCallbacksFormStore } from "@/call-controls/ScheduleCallback/callbacksForm.store";
import { useContactsStateStore } from "@/call-controls/TransferCall/vdnContacts.store";
import { getCallDetails } from "@/call-details";
import { env } from "@/config";
import { getIVRInfo } from "@/customer-info/api";
import { clearCustomerDetailsQueries } from "@/query/queryServices";
import { useAgentStore } from "@/sdk";
import { isSimulatedVoiceTask, isVoiceTask } from "@/sdk/agent/voice";
import { useSessionStore } from "@/sdk/sessions/session.store";
import { addSessionSummaryToTimeline } from "@/session-summary/components/utils";
import { sanitizeRemarkText } from "@/session-summary/sanitizeRemarkText";
import { useSessionSummaryStore } from "@/session-summary/store/useSessionSummaryStore";
import { evaluateSummaryDispatch } from "@/session-summary/summaryDispatchGate";
import { validateSummary } from "@/session-summary/validateSummary";
import { SummaryResolution } from "@/shared-types";
import { formatAgentName, getHashedValue, removeCountryCode, userCache } from "@/shared-utils";
import { buildPacingFields, resolvePacingExperimentArm } from "@/utils/buildPacingFields";

const logger = getLogger({ module: "sessionSummary" });

/**
 * Sessions whose summary has already been dispatched.
 *
 * `onSessionEnd` can run twice for one session: `AgentSdkBase.endWrapup` calls
 * `sessionsOrchestrator.completeSession` from its catch block as a cleanup fallback, and that races
 * the real `contact.onDestroy` completion event, which reaches `onTaskCompleted` on its own. Once
 * the destroy path has marked the task completed, `SessionInstance.currentTask` is `undefined`, so
 * `completeSession`'s active-task guard does not stop the second call. In production 9.26% of
 * sessions dispatched `SessionSummary_Submit` more than once, median 0.1s apart, which is a
 * code-level double invocation rather than an expert re-submitting.
 *
 * Bounded because the expert's browser stays open for a whole shift. Oldest ids are evicted first
 * (`Set` iterates in insertion order); a session that old cannot still be racing its own teardown.
 */
const dispatchedSummarySessionIds = new Set<string>();
const MAX_TRACKED_SUMMARY_SESSIONS = 500;

/**
 * Claims the single summary dispatch for a session, returning false if it was already claimed.
 *
 * Must stay synchronous and be called before the first `await` on the dispatch path: the duplicate
 * invocations arrive through independent async chains, so any await between the check and the claim
 * reopens the window this closes.
 */
const claimSummaryDispatch = (sessionId: string): boolean => {
    if (dispatchedSummarySessionIds.has(sessionId)) return false;

    dispatchedSummarySessionIds.add(sessionId);
    if (dispatchedSummarySessionIds.size > MAX_TRACKED_SUMMARY_SESSIONS) {
        const oldest = dispatchedSummarySessionIds.values().next().value;
        if (oldest !== undefined) dispatchedSummarySessionIds.delete(oldest);
    }
    return true;
};

const onSessionSummaryCompleted = async ({
    accountNumber,
    allowedAccountNumberLengths,
    autogeneratedSummary,
    blocklistedNumbers,
    blocklistedPrefixes,
    blocklistedSubscriberNumbers,
    category,
    endedSession,
    expirationTimestamp,
    extraSessionSummaryPayloadProps,
    gaiaSummaryModel,
    includesSendToPartnerMeta,
    mdn,
    partner,
    resolutionStatus,
    subcategory,
    update,
    useMdnForAccountNo,
    wrapupEndedCallback,
}: {
    accountNumber?: string;
    allowedAccountNumberLengths?: number[];
    autogeneratedSummary: string;
    blocklistedNumbers?: string[];
    blocklistedPrefixes?: string[];
    blocklistedSubscriberNumbers?: string[];
    category?: string;
    endedSession: Session;
    expirationTimestamp: number;
    extraSessionSummaryPayloadProps?: Record<string, string>;
    gaiaSummaryModel: string;
    includesSendToPartnerMeta?: boolean;
    mdn?: string;
    partner: Partner;
    resolutionStatus?: SummaryResolution;
    subcategory?: string;
    update: string;
    useMdnForAccountNo?: boolean;
    wrapupEndedCallback: ({
        session,
        type,
        message,
    }: {
        session: Session;
        type: "success" | "error";
        message: string;
    }) => void;
}) => {
    const task = Array.from(endedSession.tasks.values()).find((task) => isVoiceTask(task));
    if (!task) {
        return logger.warn(
            { sessionId: endedSession.id, kind: endedSession.kind },
            `No voice task found in ended session. Skip Session Summary send.`,
        );
    }
    // Skip Session Summary send for expert university training tasks
    if (isSimulatedVoiceTask(task)) {
        return;
    }

    const { isError, response } = validateSummary({ summary: update });
    if (isError) {
        logger.info({ response }, "Session summary validation failed");
        wrapupEndedCallback({ session: endedSession, type: "error", message: response ?? "" });
        return;
    }

    // Claimed after the checks above (all synchronous, and none of them dispatch) but before the
    // first await, so a duplicate `onSessionEnd` cannot slip past while this one is still working.
    if (!claimSummaryDispatch(endedSession.id)) {
        // debug, not info: this is skip logic that fires on ~9.26% of sessions, so it would be
        // steady noise at info. The post-release duplicate-rate check reads EDP dispatch counts
        // rather than this line, so nothing observable is lost.
        return logger.debug(
            { sessionId: endedSession.id, partner },
            "Session summary already dispatched for this session. Skipping duplicate send.",
        );
    }

    const mdnFormatted = mdn ? removeCountryCode(mdn) : "";
    const mdnHashed = mdnFormatted ? await getHashedValue(mdnFormatted) : "";

    // Gate the partner forward on the value that is actually dispatched as `accountNo`, and label
    // the stats event from the same decision.
    const remarkText = sanitizeRemarkText(update);
    const { canSendToPartner, identifierIsUsable, reason } = evaluateSummaryDispatch({
        accountNumber,
        allowedAccountNumberLengths,
        blocklistedNumbers,
        blocklistedPrefixes,
        blocklistedSubscriberNumbers,
        remarkText,
        subscriberNumber: mdnFormatted,
        useMdnForAccountNo,
    });
    if (!identifierIsUsable) {
        logger.warn(
            {
                sessionId: endedSession.id,
                partner,
                reason,
                useMdnForAccountNo,
                // The value itself is PII, so only its shape is logged.
                subscriberNumberDigits: mdnFormatted.replace(/\D/g, "").length,
            },
            canSendToPartner
                ? "Session summary identifier failed validation"
                : "Session summary not forwarded to partner tool",
        );
    }

    const sessionSummaryPayload = {
        accountNo: useMdnForAccountNo ? mdnFormatted : accountNumber,
        remarkText,
        resolutionStatus,
        ...extraSessionSummaryPayloadProps,
        ...(category ? { category } : {}),
        ...(subcategory ? { subcategory } : {}),
    };

    // Summary -> EIP. If there is a valid accountNumber EIP will also send to partner tool (ACSS/Customer Central)
    //dispatchSessionSubmit({ agentCallSid, mdn: mdnFormatted, mdnHashed, sessionSummaryPayload });
    const sessionSummarySubmitPromise = getRootDispatcher()
        .withIdentities({
            CallSid: task.agentCallId,
            mdn: mdnFormatted,
            HashedMdn: mdnHashed,
            SupportSessionId: endedSession.id,
            SessionId: endedSession.id,
        })
        .withMeta({
            Client: partner,
            Channel: "voice",
            SkipMixpanel: true,
            SkipEDP: false,
            SkipOpenSearch: true,
            // `sendToPartner` is what the enterprise-eventing rule `ExWoSupportSessionClosedForATTNotes`
            // matches on, so omitting it stops the partner notes call while the summary still reaches EDP.
            ...(includesSendToPartnerMeta && canSendToPartner ? { sendToPartner: "true" } : {}),
        })
        .withExtra({ partner, taskType: task.type })
        // SessionSummary_Submit is the specific event_name that will be forwarded from EIP to the partner tool (ACSS/OPUS)
        .dispatchBusinessEvent(`SessionSummary_Submit`, sessionSummaryPayload);

    const shouldSendToPartnerTool = identifierIsUsable;
    const gaiaCallCategory = useCallCategoryStore.getState().callCategory;
    // summary send event WITHOUT remark text (no PII so this can be sent to OpenSearch)
    void getRootDispatcher()
        .withIdentities({ CallSid: task.agentCallId, SupportSessionId: endedSession.id, SessionId: endedSession.id })
        .withMeta({ Client: partner, Channel: "voice", SkipEDP: false })
        .dispatchBusinessEvent(`SessionSummary_Stats`, {
            summaryWasAutoGenerated: autogeneratedSummary !== "",
            summaryWasEdited: update !== autogeneratedSummary,
            summaryWasSubmittedViaPartnerTool: shouldSendToPartnerTool ?? false,
            summaryWasCopiedToClipboard: !shouldSendToPartnerTool,
            summaryLength: update?.length ?? 0,
            // TODO: send remaining time or assume there is none left
            // timeRanOut: timeRanOut ?? timeRemainingInMs < 1,
            timeRemainingInMs: expirationTimestamp,
            gaiaSummaryModel,
            hasValidMdn: !!mdn,
            ...(gaiaCallCategory ? { gaiaCallCategory } : {}),
        });

    await sessionSummarySubmitPromise;
    wrapupEndedCallback({ session: endedSession, type: "success", message: "Summary sent" });
};

const emitSessionEndEvent = async (
    endedSession: Session,
    partner: Partner,
    wrapupEndedCallback: ({
        session,
        type,
        message,
    }: {
        session: Session;
        type: "success" | "error";
        message: string;
    }) => void,
) => {
    const endedSessionId = endedSession.id;
    const partnerConfig = getAppConfigSingleton().getPartnersConfig(partner);

    const { summary, resolutionStatus, autogeneratedSummary, category, subcategory } =
        useSessionSummaryStore.getState();
    const {
        accountNumber,
        email,
        orderId,
        fullName,
        mdn: verifiedMdn,
    } = useVerifiedCustomerStore.getState().getVerifiedCustomer(endedSessionId).customer ?? {};
    const taskMdn = Array.from(endedSession.tasks.values()).find(isVoiceTask)?.mdn;
    const mdn = verifiedMdn || taskMdn;
    const agentName = formatAgentName(useAgentStore.getState().agentName);

    const shouldRedactNotes = partnerConfig.features.postCallNotepadRedaction;

    // Collect pacing data — gate on isPacingEnabled feature flag
    const gaiaCallCategory = useCallCategoryStore.getState().callCategory;
    const products = useProductOfferStore.getState().getProducts(endedSessionId);
    const eligibleProducts = products.filter((p) => p.status === EligibilityStatus.Eligible);
    const featureFlagContext = {
        appName: "expert-workspace",
        employeeId: userCache.employeeId,
        environment: env.mode,
        channel: env.channel,
        partner,
        source: "expert-ui",
    };
    const isUpdatedEndSessionFlowEnabled = !!(await getFeature<boolean>(
        "isUpdatedEndSessionFlowEnabled",
        featureFlagContext,
    ));
    const [isPacingEnabled, isGlowBarPacingEnabled, isVoiceWorkspaceRedesignV2Enabled] = await Promise.all([
        getFeature<boolean>("isPacingEnabled", featureFlagContext),
        getFeature<boolean>("isGlowBarPacingEnabled", featureFlagContext),
        getFeature<boolean>("EWP-isVoiceWorkspaceRedesignV2Enabled", featureFlagContext),
    ]);
    const pacingConfig = partnerConfig.pacingTargets
        ? { ...partnerConfig.pacingTargets, enabled: isPacingEnabled === true }
        : undefined;
    const pacingFields = buildPacingFields({
        pacingConfig,
        // The partner switch off the raw config: `pacingConfig.enabled` is overwritten above.
        experimentArm: resolvePacingExperimentArm({
            isPacingEnabled,
            pacingTargetsEnabled: partnerConfig.pacingTargets?.enabled,
            isGlowBarPacingEnabled,
            isVoiceWorkspaceRedesignV2Enabled,
        }),
        gaiaCallCategory,
        salesEligible: eligibleProducts.length > 0,
        eligibleProductCount: eligibleProducts.length,
        hasPrimaryProduct: eligibleProducts.some((p) => p.isPrimaryProduct),
        actualSeconds: endedSession.startedTimestamp
            ? Math.floor((Date.now() - endedSession.startedTimestamp) / 1000)
            : undefined,
    });

    void getRootDispatcher()
        .withMeta({ Client: partner, Channel: env.channel, SkipEDP: false, SkipMixpanel: false })
        .withIdentities({
            SessionId: endedSessionId,
            SupportSessionId: endedSessionId,
            EmployeeName: agentName,
        })
        .dispatchBusinessEvent("SupportSessionClosed", {
            EndCallReason: resolutionStatus ?? "not resolved",
            EndCallSubReason: "",
            SupportSessionNotes: summary,
            AutoGeneratedSummary: autogeneratedSummary,
            ProgramName: partnerConfig?.programs?.[0]?.name,
            CustomerName: fullName ?? "",
            ...(category ? { CallCategory: category } : {}),
            ...(subcategory ? { CallSubCategory: subcategory } : {}),
            ...pacingFields,
        });

    void getRootDispatcher()
        .withMeta({ Client: partner, Channel: env.channel, SkipEDP: false, SkipMixpanel: true, SkipOpenSearch: true })
        .withIdentity("SessionId", endedSessionId)
        .withIdentity("SupportSessionId", endedSessionId)
        .dispatchBusinessEvent("AdditionalSupportSessionDetails", {
            ...(email != null && email.length > 0 ? { Email: email } : {}),
            ...(orderId != null && orderId.length > 0 ? { OrderId: orderId } : {}),
        });

    const { note, setNote } = useExpertNoteStore.getState();
    if (note) {
        const piiScrubbedNote = await removePii(note);
        let didRedactNotes = false;
        if (shouldRedactNotes && note !== piiScrubbedNote) {
            didRedactNotes = true;
            setNote(piiScrubbedNote);
        }
        void getRootDispatcher()
            .withMeta({ Client: partner, Channel: "voice", SkipEDP: false, SkipMixpanel: true, SkipOpenSearch: true })
            .withIdentity("SessionId", endedSessionId)
            .withIdentity("SupportSessionId", endedSessionId)
            .dispatchBusinessEvent("SaveExpertNote", {
                note: truncateAnalyticsText(piiScrubbedNote),
                didRedactNotes,
            });
    }

    const { gaiaSummaryModel } = partnerConfig;

    /* Only send session summary from session close if the new end-session-flow is NOT enabled.
        The new flow submits from a component as soon as the summary is generated.
    */
    if (!isUpdatedEndSessionFlowEnabled) {
        const [includesSendToPartnerMeta, useMdnForAccountNo] = await Promise.all([
            getFeature("includesSendToPartnerMeta", featureFlagContext),
            getFeature("useMdnForAccountNo", featureFlagContext),
        ]);

        void onSessionSummaryCompleted({
            accountNumber,
            allowedAccountNumberLengths: partnerConfig.allowedAccountNumberLengths,
            autogeneratedSummary,
            blocklistedNumbers: partnerConfig.blocklistedNumbers,
            blocklistedPrefixes: partnerConfig.blocklistedPrefixes,
            blocklistedSubscriberNumbers: partnerConfig.blocklistedSubscriberNumbers,
            category,
            endedSession,
            expirationTimestamp: 0,
            extraSessionSummaryPayloadProps: partnerConfig.extraSessionSummaryPayloadProps,
            gaiaSummaryModel,
            includesSendToPartnerMeta: !!includesSendToPartnerMeta,
            mdn,
            partner,
            resolutionStatus,
            subcategory,
            update: summary,
            useMdnForAccountNo: !!useMdnForAccountNo,
            wrapupEndedCallback,
        });
    }
};

export const onSessionEnd = (
    endedSession: Session,
    endedSessionPartner: Partner,
    wrapupEndedCallback: ({
        session,
        type,
        message,
    }: {
        session: Session;
        type: "success" | "error";
        message: string;
    }) => void,
) => {
    /*
     * We need to reset a few stores when a new session comes in
     * Reset customer verification status
     * Reset sale session
     * Reset CSD Call Details
     */
    // Legacy SupportSessionClosed event
    void emitSessionEndEvent(endedSession, endedSessionPartner, wrapupEndedCallback);
    void endC360InteractionLine(
        endedSession,
        endedSessionPartner,
        useSessionSummaryStore.getState().autogeneratedSummary,
    );
    clearC360CustomerSession(endedSession.id);
    addSessionSummaryToTimeline(endedSession.id);
    // Reset stores
    useVerificationFlowStore.getState().resetSteps(endedSession.id);
    useCallDetailsStore.getState().resetCallDetails();
    useProductOfferStore.getState().endSaleSession(endedSession.id);
    useVerifiedCustomerStore.getState().reset(endedSession.id);
    useDetectedCuesStore.getState().resetEvents(endedSession.id);
    useIntegratedFlowStore.getState().resetIntegratedStepStore(endedSession.id);
    useOfferChecklistStore.getState().resetData(endedSession.id);
    useCallbacksFormStore.getState().resetState();
    useCallCategoryStore.getState().reset();
    useContactsStateStore.getState().resetState();
    useDeviceInfoStore.getState().resetDevices(endedSession.id);
    clearProbedSession(endedSession.id);
    // Clear any persisted disclosure-language selection so it never leaks into the next session.
    clearLanguageStoreStorage();
    // Ensure customer details are refetched for the next session even if the MDN is the same.
    clearCustomerDetailsQueries();

    useGuideStore.getState().deleteSession(endedSession.id);
    useAssistPanelStore.getState().deleteSession(endedSession.id);

    if (env.channel === "chat") {
        useSessionStore.getState().clearInactiveSession(endedSession.id);
    }
};

export const onSessionStart = async (newSession: Session, newSessionPartner: Partner) => {
    if (!isVoiceTask(newSession.currentTask)) return;

    const task = newSession.currentTask;
    // The lookup needs the line, so it runs once the start has finished; neither throws.
    void startC360Interaction(newSession, newSessionPartner).then(() =>
        linkC360CustomerAtCallStart(newSession, newSessionPartner),
    );

    useSessionSummaryStore.getState().resetSessionSummary();
    useGuideStore.getState().resetGuide(newSession.id);
    // fallback to `id` if no `asurionCallId`; Channel Share Data uses Amazon Connect Contact ID as primary key
    const data = await getCallDetails(task.asurionCallId ?? task.id, task.partner);
    useCallDetailsStore.getState().setCallDetails(data ?? {});

    const voiceTaskConvProp = task.conversations;
    const tfnDnis = voiceTaskConvProp ? voiceTaskConvProp.conversation_attribute_8 : null;

    await getRootDispatcher()
        .withExtra({
            UUI_ANI: data?.ANI,
            CallDirection: task.callDirection,
            CVPCallId: task.asurionCallId,
            TfnDnis: tfnDnis,
            ContactSource: data?.ClientCode,
            ICMRouterKey: data?.RouterCallKey,
            ICMRouterDay: data?.RouterCallDay,
            Locale: task.locale || "undefined",
            partner: newSessionPartner,
        })
        .withMeta({ Client: newSessionPartner, Channel: "Call" })
        .dispatchBusinessEvent("SupportSessionStarted");

    const ivrInfo = getIVRInfo(task);
    if (ivrInfo && Object.values(ivrInfo).filter((value) => !!value).length > 0) {
        const extraData = {
            includesSummary: !!ivrInfo.summary,
            includesIntent: !!ivrInfo.intent,
            includesEmail: !!ivrInfo.email,
            includesCustomerName: !!ivrInfo.customerName,
            includesProductTitle: !!ivrInfo.productTitle,
            includesBrandPhoneNumber: !!ivrInfo.brandPhoneNumber,
            includesSupportPhoneNumber: !!ivrInfo.supportPhoneNumber,
            includesDevice: !!ivrInfo.device,
            includesOrderNumber: !!ivrInfo.orderNumber,
            includesProductLink: !!ivrInfo.productLink,
            includesConversationalSummary: !!ivrInfo.conversationalSummary,
            includesAccountId: !!ivrInfo.accountId,
            includesWarmTransferMessage: !!ivrInfo.warmTransferMessage,
            includesIsWarmHandoffEnabled: ivrInfo.isWarmHandoffEnabled?.toLowerCase() === "true",
            includesExpertScript: !!ivrInfo.expertScript,
            includesHandoffRecordingRuntimeMs: !!ivrInfo.handoffRecordingRuntimeMs,
            includesInteractionId: !!ivrInfo.interactionId,
            intent: ivrInfo.intent,
            mappedIntent: ivrInfo.intent ? getMappedIntent(ivrInfo.intent) : undefined,
            summary: ivrInfo.summary,
            device: ivrInfo.device,
            productLink: ivrInfo.productLink,
            productTitle: ivrInfo.productTitle,
            handoffRecordingRuntimeMs: ivrInfo.handoffRecordingRuntimeMs,
            isWarmHandoffEnabled: ivrInfo.isWarmHandoffEnabled,
            expertScript: ivrInfo.expertScript,
            interactionId: ivrInfo.interactionId,
        };
        void getRootDispatcher().dispatchBusinessEvent("IVRInfoReceived", { ...extraData });
        trackFullStoryEvent("IVRInfoReceived", { ...extraData });
    }
};
